import { Box, Button, Icon, Input, InputGroup, InputLeftElement, Text } from '@chakra-ui/react';
import { FiBarChart2, FiPlus } from 'react-icons/fi';
import { MdSearch } from 'react-icons/md';
import { PageContainer } from '@/client/components/layout/PageContainer';
import { LoadingSpinner } from '@/design-system/components/LoadingSpinner';
import { EmptyState } from '@/design-system/components/EmptyState';
import { ConfirmDialog } from '@/design-system/components/ConfirmDialog';
import { ReportGrid } from '../components/ReportGrid';
import { ShareDialog } from '../components/ShareDialog';
import { DashboardBrowser } from '../components/DashboardBrowser';
import { ReportDialog } from '../components/ReportDialog';
import { useReportsPage } from '../hooks';

export function ReportsPage() {
  const p = useReportsPage();

  if (p.isLoading) {
    return <LoadingSpinner message="Loading reports..." />;
  }

  if (p.viewingReport) {
    return <DashboardBrowser report={p.viewingReport} onClose={() => p.setViewingReport(null)} />;
  }

  return (
    <PageContainer
      title="Reports"
      description="View and share reports with team members"
      action={
        p.canAddReport ? (
          <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={p.handleAddReport} size="lg">
            Add Report
          </Button>
        ) : undefined
      }
    >
      {p.reports && p.reports.items.length > 0 && (
        <Box>
          <InputGroup size="lg">
            <InputLeftElement pointerEvents="none">
              <Icon as={MdSearch} color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search organizations by name, email, or location..."
              value={p.searchQuery}
              onChange={(e) => p.setSearchQuery(e.target.value)}
              bg="bg-surface" borderColor="border-muted"
              _hover={{ borderColor: 'border-hover' }}
              _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
            />
          </InputGroup>
          {p.searchQuery && (
            <Text fontSize="sm" color="gray.600" mt={2}>
              Found {p.filteredReports.length} report{p.filteredReports.length !== 1 ? 's' : ''}
            </Text>
          )}
        </Box>
      )}

      {!p.reports || p.reports.items.length === 0 ? (
        <EmptyState
          icon={<FiBarChart2 size={48} />}
          title="No reports available"
          description="There are no reports configured for this organization yet."
          action={p.canAddReport ? { label: 'Add Report', onClick: p.handleAddReport } : undefined}
        />
      ) : (
        <ReportGrid
          reports={p.reports.items}
          onViewReport={p.handleViewReport}
          onShareReport={p.handleShareReport}
          onEditReport={p.handleEditReport}
          onDeleteReport={p.handleDeleteReport}
          canShare={p.canShare}
          isSuperAdmin={p.isSuperAdmin}
        />
      )}

      {(p.canAddReport || p.canEditReport) && (
        <ReportDialog
          isOpen={p.isReportDialogOpen}
          onClose={() => { p.setIsReportDialogOpen(false); p.setEditingReport(null); }}
          initialData={p.editingReport ? {
            title: p.editingReport.title,
            description: p.editingReport.description,
            public_url: p.editingReport.public_url,
          } : undefined}
          onSubmit={p.handleReportSubmit}
        />
      )}

      {p.canDeleteReport && (
        <ConfirmDialog
          isOpen={p.isDeleteDialogOpen}
          onClose={() => { p.setIsDeleteDialogOpen(false); p.setDeletingReport(null); }}
          onConfirm={p.handleConfirmDelete}
          title="Delete Report"
          message={`Are you sure you want to delete "${p.deletingReport?.title}"? This action cannot be undone.`}
          confirmText="Delete"
          isLoading={p.isDeleting}
        />
      )}

      {p.canShare && p.teamMembers && (
        <ShareDialog
          isOpen={p.isShareDialogOpen}
          onClose={() => { p.setIsShareDialogOpen(false); p.setSelectedReport(null); }}
          report={p.selectedReport}
          teamMembers={p.teamMembers}
          onShare={p.handleShareSubmit}
          isSharing={p.isSharing}
        />
      )}
    </PageContainer>
  );
}
